package com.essntl.features.message.ui.all_conversation

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.PaginationState
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.core.utils.viewmodel.getPaginationData
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.message.domain.repository.ConversationRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AllConversationViewModel(
    private val conversationRepository: ConversationRepository,
    private val clientRepository: ClientRepository,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<AllConversationState>,
) : ViewModel {
    fun onEvent(event: AllConversationEvent) {
        when (event) {
            is AllConversationEvent.SelectConversation -> selectConversation(event)
            is AllConversationEvent.UpdateChatLastMessage -> updateChatLastMessage(event)
            is AllConversationEvent.ChangeFilterOptions -> changeFilterOptions(event)
            is AllConversationEvent.GetAllWhapiChats -> getAllConversations()
            is AllConversationEvent.GetNextConversationsPage -> getNextConversationsPage()
        }
    }

    private fun getAllConversations() {
        state.update {
            it.copy(
                getAllWhapiChats = PaginationState(
                    isLoading = true,
                    data = state.value.getAllWhapiChats.data,
                ),
            )
        }

        scope.launch {
            getPaginationData(
                getData = {
                    conversationRepository.getWhapiChats(0)
                },
                setState = { },
                onSuccess = { page, chats ->
                    state.update {
                        it.copy(
                            getAllWhapiChats = PaginationState(isLoading = false, data = chats, loadedPages = page),
                        )
                    }

                    val phoneNumbers = chats.map { it.phoneNumber }

                    getClientByPhoneNumber(phoneNumbers)

                    getAllGroups()

                },
                onFailure = {
                    it.printStackTrace()
                    state.update {
                        it.copy(
                            getAllWhapiChats = PaginationState(
                                isFailure = true,
                                isLoading = false,
                                data = emptyList(),
                            ),
                        )
                    }
                },
                ignoreLoading = true,
            )
        }
    }

    private fun getAllGroups() {
        scope.launch {
            getData(
                getData = { conversationRepository.getWhapiGroups() },
                getState = { state.value.getAllWhapiGroups },
                setState = { newState -> state.update { it.copy(getAllWhapiGroups = newState) } },
            )
        }
    }

    private fun getNextConversationsPage() {
        scope.launch {
            getPaginationData(
                getData = {
                    val nextPage = state.value.getAllWhapiChats.loadedPages.toLong()
                    conversationRepository.getWhapiChats(nextPage)
                },
                getState = { state.value.getAllWhapiChats },
                setState = { newState -> state.value = state.value.copy(getAllWhapiChats = newState) },
                onSuccess = { _, chats ->
                    val phoneNumbers = chats.map { it.phoneNumber }
                    getClientByPhoneNumber(phoneNumbers)
                },
            )
        }
    }

    private fun getClientByPhoneNumber(phoneNumbers: List<String>) =
        getData(
            getData = { clientRepository.getByPhoneNumbers(phoneNumbers.toSet()) },
            getState = { state.value.getAllClient },
            setState = { newState -> state.update { it.copy(getAllClient = newState) } },
        )

    private fun selectConversation(event: AllConversationEvent.SelectConversation) {
        state.update { it.copy(selectedConversationId = event.conversationId) }

        getData(
            getData = {
                conversationRepository.updateWhapiChat(
                    chatId = event.conversationId,
                    markUnread = false,
                )
            },
            setState = { },
            onSuccess = {
                state.update { state ->
                    state.copy(
                        getAllWhapiChats = state.getAllWhapiChats.copy(
                            data = state.getAllWhapiChats.data.map { chat ->
                                if (chat.id == event.conversationId)
                                    chat.copy(unreadCount = null)
                                else
                                    chat
                            },
                        ),
                        getAllWhapiGroups = state.getAllWhapiGroups.copy(
                            data = state.getAllWhapiGroups.data.orEmpty().map { chat ->
                                if (chat.id == event.conversationId)
                                    chat.copy(unreadCount = null)
                                else
                                    chat
                            },
                        ),
                    )
                }
            },
        )
    }

    private fun updateChatLastMessage(event: AllConversationEvent.UpdateChatLastMessage) {
        val chat = state.value.getAllWhapiChats.data.firstOrNull { it.id == event.chatId } ?: return
        var updatedChatsList = state.value.getAllWhapiChats.data.filter { it.id != event.chatId }
        updatedChatsList = listOf(
            chat.copy(
                lastMessage = event.lastMessage,
                timestamp = event.lastMessage.timestamp,
            ),
        ) + updatedChatsList

        state.update { state ->
            state.copy(
                getAllWhapiChats = state.getAllWhapiChats.copy(data = updatedChatsList),
            )
        }
    }

    private fun changeFilterOptions(event: AllConversationEvent.ChangeFilterOptions) {
        state.update {
            it.copy(filterOptions = event.filterOptions)
        }
        // TODO() Add filter logic
    }
}